/**
 * Turns the workouts already in `AppState` into the per-exercise session
 * history the engine consumes.
 *
 * No new network call: `sbLoadWorkouts` (core.js:2399) already selects
 * `workouts(*, workout_exercises(*, exercise:exercises(*), sets(*)))`, so every
 * set of every workout is in memory by the time the dashboard paints.
 * `normalizeWorkout` (core.js:2366) has already sorted sets by `set_number` and
 * mapped them to `{ weight_kg, reps, set_type, completed, … }` — the exact shape
 * `engine.js` expects. This module only has to group and order.
 *
 * Phase 2's "previous values" column reads from here too. That is deliberate:
 * "what did I do last time on this lift" should have one implementation, not
 * one per feature.
 *
 * A correction to the plan, from reading the source: it says the engine must
 * call `getEffectiveWeight` "or every calisthenics prescription reads as zero".
 * That is no longer true — `getEffectiveWeight` (core.js:161) is now the
 * identity function on its weight argument, because bodyweight exercises log
 * actual weight directly (bodyweight, or bodyweight + vest). Routing through it
 * would add a dependency on a legacy global that does nothing. We read
 * `weight_kg` directly and let `increments.js` handle the genuinely
 * load-less equipment (bodyweight, band) by progressing reps instead.
 */

import { legacy } from '../../runtime/legacy.js';

/** Sessions the engine should ignore entirely. */
function isUsableSet(set) {
  return set && (set.set_type || 'working') === 'working' && (set.reps || 0) > 0;
}

/**
 * Every past session for one exercise, oldest first, in engine shape.
 *
 * @param {string} exerciseId
 * @param {object} [options]
 * @param {number} [options.limit] Keep only the most recent N sessions.
 * @returns {Array<{ date: string, workoutId: string, sets: Array }>}
 */
export function getExerciseHistory(exerciseId, { limit } = {}) {
  const AppState = legacy('AppState');
  const workouts = AppState?.workouts || [];

  const sessions = [];
  workouts.forEach((workout) => {
    (workout.exercises || []).forEach((exercise) => {
      if (exercise.exercise_id !== exerciseId) return;
      const sets = (exercise.sets || []).filter(isUsableSet);
      if (!sets.length) return;
      sessions.push({ date: workout.date, workoutId: workout.id, sets });
    });
  });

  // `sbLoadWorkouts` orders newest-first and the engine reads oldest-first, but
  // sorting explicitly rather than reversing keeps this correct if the load
  // order ever changes — and demo/seed data is not guaranteed to be ordered.
  sessions.sort((a, b) => new Date(a.date) - new Date(b.date));

  return typeof limit === 'number' ? sessions.slice(-limit) : sessions;
}

/** The most recent session for an exercise, or null. Phase 2's "previous" column. */
export function getLastSession(exerciseId) {
  const sessions = getExerciseHistory(exerciseId, { limit: 1 });
  return sessions.length ? sessions[0] : null;
}

/** Exercise catalogue row, for equipment and muscle group. */
export function getExercise(exerciseId) {
  const AppState = legacy('AppState');
  return (AppState?.exercises || []).find((e) => e.id === exerciseId) || null;
}

/**
 * The exercises worth prescribing for today: most recently trained first,
 * de-duplicated, drawn from the last few sessions.
 *
 * There is no "planned session" concept in the app yet — no routine template,
 * no scheduled split — so "today's session" can only be inferred from what the
 * user actually trains. Recency is the honest proxy, and it degrades gracefully:
 * a user with one workout sees one lift, not a guess at a program.
 */
export function getRecentExercises({ withinLastSessions = 3, max = 6 } = {}) {
  const AppState = legacy('AppState');
  const workouts = [...(AppState?.workouts || [])]
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, withinLastSessions);

  const seen = new Set();
  const out = [];
  workouts.forEach((workout) => {
    (workout.exercises || []).forEach((exercise) => {
      const id = exercise.exercise_id;
      if (!id || seen.has(id)) return;
      if (!(exercise.sets || []).some(isUsableSet)) return;
      seen.add(id);
      out.push({ exerciseId: id, name: exercise.exercise_name, muscleGroup: exercise.muscle_group });
    });
  });

  return out.slice(0, max);
}

/**
 * Weekly working-set count per muscle group, for the engine's volume landmarks.
 * Counts the trailing 7 days from now.
 */
export function getWeeklySetsByMuscleGroup(now = Date.now()) {
  const AppState = legacy('AppState');
  const cutoff = now - 7 * 24 * 60 * 60 * 1000;
  const counts = {};

  (AppState?.workouts || []).forEach((workout) => {
    if (new Date(workout.date).getTime() < cutoff) return;
    (workout.exercises || []).forEach((exercise) => {
      const group = exercise.muscle_group;
      if (!group) return;
      const n = (exercise.sets || []).filter(isUsableSet).length;
      if (n) counts[group] = (counts[group] || 0) + n;
    });
  });

  return counts;
}
